import "./ConsultantForms.scss";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useState, useMemo } from "react";
import {
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
  SCHOOL_LEVEL_OPTIONS,
} from "constants/forms";
import { LANG, RECRUITMENT_METHOD, INPUTS_IDS } from "constants/constants";
import InfoContext from "pages/InfoContext";
import {
  renderInputAddress,
  renderInputBirthtown,
  renderInputChildren,
  renderInputCooptedBy,
  renderInputNationality,
  renderInputPicture,
  renderInputRecruitmentMode,
  renderRefererExternal,
  renderInputYear,
  renderInputYearsExperience,
  renderPhonenumber,
  renderInputDegree,
  renderSchool,
  renderSchoolLevel,
} from "../CommonInputs";
import propTypes from "prop-types";
import { formatNationalityValue, getErrorCellClass } from "utils/helper";

const ConsultantPersonalInfoForm = ({
  control,
  errors,
  readOnly,
  register,
  setValue,
  watch,
  unregister,
  cellHighlight,
}) => {
  const {
    lang,
    personsByProfileList,
    getOptions,
    recruitmentMethodList,
    id,
    isMobile,
    nationalities,
    picture,
    createFile,
    uploadProfilePicture,
    createdFile,
    setCreatedFile,
    setCode,
    setStatus,
    apiErrorResponse,
    tempUserId,
  } = useContext(InfoContext);
  const [recruitmentMethods, setRecruitmentMethods] = useState([]);
  const experience_comment = watch("experience_comment", "");
  const nationality = watch("nationality", []);
  const recruitment_mode_id = watch("recruitment_mode_id", []);
  const referer_internal_id = watch("referer_internal_id", []);

  // Memoize picture value to prevent unnecessary re-renders
  const pictureValue = useMemo(() => {
    return createdFile?.picture_file_id || createdFile?.uri ? picture : "";
  }, [createdFile?.picture_file_id, createdFile?.uri, picture]);

  useEffect(() => {
    const formattedValue = formatNationalityValue(
      nationality,
      nationalities,
      lang
    );
    if (
      formattedValue &&
      (!Array.isArray(nationality) ||
        nationality.length === 0 ||
        nationality[0].value !== formattedValue.value ||
        nationality[0].label !== formattedValue.label)
    ) {
      setValue("nationality", [formattedValue]);
    }
  }, [lang, nationalities, nationality, setValue]);

  useEffect(() => {
    if (recruitmentMethodList.length > 0 && lang) {
      setRecruitmentMethods(
        recruitmentMethodList
          ?.filter((e) => e.invisible === false)
          .map((elm) => ({
            label: lang === LANG.EN ? elm.en : elm.fr,
            value: elm.id,
            type: "recruitment_mode",
          }))
      );
    }
  }, [lang, recruitmentMethodList]);

  useEffect(() => {
    setValue(
      "picture_file_id",
      createdFile?.picture_file_id || createdFile?.uri
    );
  }, [createdFile]);

  useEffect(() => {
    if (recruitment_mode_id[0]?.value === RECRUITMENT_METHOD.REFERER_EXTERNAL) {
      unregister("referer_internal_id");
    } else if (
      recruitment_mode_id[0]?.value === RECRUITMENT_METHOD.REFERER_INTERNAL
    ) {
      unregister("referer_external");
    } else {
      unregister("referer_internal_id");
      unregister("referer_external");
    }
  }, [recruitment_mode_id]);

  const displayInputAddress = () => {
    return (
      <div
        className={`corner-right ${
          (!watch("address") && cellHighlight) ||
          errors?.["address"] ||
          apiErrorResponse?.["address"]
            ? "errorCellColor"
            : ""
        }`}
        style={{ width: "304px" }}
      >
        {renderInputAddress({
          control,
          errors,
          label: CONSULTANT_FORM_LABELS.address,
          placeholder: CONSULTANT_FORM_PLACEHOLDERS.address,
          readOnly,
          register,
          id: CONSULTANT_FORM_LABELS.address.id,
          errorMessage: apiErrorResponse?.["address"] || errors?.["address"]?.message,
        })}
      </div>
    );
  };

  const formFieldsLine1 = [
    {
      id: "birth_place",
      render: renderInputBirthtown,
      label: CONSULTANT_FORM_LABELS.birth_place,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.birth_place,
      errorMessage: apiErrorResponse?.["birth_place"],
      style: { borderRadius: id ? "6px 0 0 0" : "unset !important" },
    },
    {
      id: "nationality",
      render: renderInputNationality,
      label: CONSULTANT_FORM_LABELS.nationality_id,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.nationality,
      nationalities,
      value: nationality,
      lang,
      name: "nationality",
      errors,
      setValue,
      errorMessage: apiErrorResponse?.["nationality"] || errors?.["nationality"]?.message,
    },
    {
      id: "children_information",
      render: renderInputChildren,
      label: CONSULTANT_FORM_LABELS.children_information,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.children_information,
      errorMessage: apiErrorResponse?.["children_information"],
      hasError: false,
    },
    {
      id: "phone_number",
      render: renderPhonenumber,
      label: CONSULTANT_FORM_LABELS.phone_number,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.phone_number,
      required: true,
      errorMessage:
        apiErrorResponse?.["phone_number"] ||
        (watch("phone_number") && errors?.["phone_number"]?.message),
    },
  ];

  const formFieldsLine2 = [
    {
      id: "school",
      render: renderSchool,
      label: CONSULTANT_FORM_LABELS.school,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.school,
      errorMessage: apiErrorResponse?.["school"] || errors?.["school"]?.message,
    },
    {
      id: "school_level",
      render: renderSchoolLevel,
      label: CONSULTANT_FORM_LABELS.school_level,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.school_level,
      setValue,
      options: SCHOOL_LEVEL_OPTIONS,
    },
    {
      id: "degree",
      render: renderInputDegree,
      label: CONSULTANT_FORM_LABELS.degree,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.degree,
      errorMessage: apiErrorResponse?.["degree"] || errors?.["degree"]?.message,
    },
    {
      id: "degree_year",
      render: renderInputYear,
      label: CONSULTANT_FORM_LABELS.degree_year,
      placeholder: CONSULTANT_FORM_PLACEHOLDERS.degree_year,
    },
  ];

  const renderLine1 = () => (
    <>
      {formFieldsLine1.map((field) => {
        const {
          id,
          style,
          label,
          placeholder,
          errorMessage,
          nationalities,
          value,
          lang,
          options,
          name,
          required,
        } = field || {};
        return (
          <div
            className={`cell-padding ${getErrorCellClass(
              field,
              errors,
              watch,
              cellHighlight
            )}`}
            key={id}
            style={style}
          >
            {field.render({
              control,
              errors,
              label,
              placeholder,
              readOnly,
              register,
              setValue,
              watch,
              errorMessage,
              nationalities,
              value,
              lang,
              options,
              name,
              required,
            })}
          </div>
        );
      })}
      {isMobile && displayInputAddress()}
    </>
  );

  const renderLine2 = () => (
    <>
      {formFieldsLine2.map((field) => (
        <div
          className={`cell-padding ${getErrorCellClass(
            field,
            errors,
            watch,
            cellHighlight
          )}`}
          key={field.id}
        >
          {field.render({
            control,
            errors,
            label: field.label,
            placeholder: field.placeholder,
            readOnly,
            register,
            setValue,
            watch,
            errorMessage: field.errorMessage,
            options: field.options,
          })}
        </div>
      ))}
    </>
  );

  useEffect(() => {
    if (recruitment_mode_id?.length > 0) {
      switch (recruitment_mode_id[0]?.value) {
        case RECRUITMENT_METHOD.REFERER_EXTERNAL:
          setValue("referer_internal_id", "");
          break;
        case RECRUITMENT_METHOD.REFERER_INTERNAL:
          setValue("referer_external", "");
          break;
        default:
          setValue("referer_internal_id", "");
          setValue("referer_external", "");
          break;
      }
    }
  }, [recruitment_mode_id]);

  return (
    <div className="section-1">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage id="who_s_she_he" defaultMessage="WHO'S SHE/HE ?" />
        }
      />
      <div className="consultant-personal-info" style={{ width: "100%" }}>
        {!id && (
          <div className="corner-left" style={{ width: "138px" }}>
            {renderInputPicture({
              label: CONSULTANT_FORM_LABELS.picture_file_id,
              readOnly,
              id: INPUTS_IDS.CONSULTANT,
              value: pictureValue,
              createFile,
              uploadProfilePicture,
              setCreatedFile,
              setCode,
              setStatus,
              userId: tempUserId,
              createdFile,
            })}
          </div>
        )}
        <div className="center-block">
          <div className="line-1">{renderLine1()}</div>
          <div className="line-2">{renderLine2()}</div>
        </div>
        {!isMobile && displayInputAddress()}
      </div>
      <div className="consultant-personal-form-last-block">
        <div
          className={`cell-padding ${
            !watch("recruitment_experience") &&
            cellHighlight &&
            "errorCellColor"
          }`}
        >
          {renderInputYearsExperience({
            comment: experience_comment,
            commentName: "experience_comment",
            control,
            errors,
            label: CONSULTANT_FORM_LABELS.years_experience,
            placeholder: CONSULTANT_FORM_PLACEHOLDERS.years_experience,
            readOnly,
            register,
            setValue,
            name: "recruitment_experience",
            errorMessage:
              apiErrorResponse?.recruitment_experience ||
              errors?.recruitment_experience?.message ||
              (!watch("recruitment_experience") && cellHighlight
                ? "mandatory_input"
                : undefined),
          })}
        </div>
        <div
          className={`cell-padding ${
            watch("recruitment_mode_id").length === 0 &&
            cellHighlight &&
            "errorCellColor"
          }`}
        >
          {renderInputRecruitmentMode({
            control,
            errors,
            label: CONSULTANT_FORM_LABELS.recruitment_mode_id,
            placeholder: CONSULTANT_FORM_PLACEHOLDERS.recruitment_mode_id,
            readOnly,
            recruitmentMethods,
            required: true,
            register,
            setValue,
            value: recruitment_mode_id,
          })}
        </div>
        {recruitment_mode_id &&
          recruitment_mode_id[0]?.value ===
            RECRUITMENT_METHOD.REFERER_INTERNAL && (
            <div className="cell-padding">
              {renderInputCooptedBy({
                control,
                errors,
                getOptions,
                label: CONSULTANT_FORM_LABELS.referer_internal_id,
                placeholder: CONSULTANT_FORM_PLACEHOLDERS.referer_internal_id,
                readOnly,
                recruitment_mode_id,
                register,
                setValue,
                value: referer_internal_id,
                personsList: personsByProfileList,
              })}
            </div>
          )}
        {recruitment_mode_id &&
          recruitment_mode_id[0]?.value ===
            RECRUITMENT_METHOD.REFERER_EXTERNAL && (
            <div className="cell-padding">
              {renderRefererExternal({
                control,
                label: CONSULTANT_FORM_LABELS.referer_external,
                placeholder: CONSULTANT_FORM_PLACEHOLDERS.referer_external,
                readOnly,
                register,
                errors,
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default ConsultantPersonalInfoForm;

ConsultantPersonalInfoForm.propTypes = {
  control: propTypes.object,
  errors: propTypes.object,
  readOnly: propTypes.bool,
  register: propTypes.func,
  setValue: propTypes.func,
  watch: propTypes.func,
  unregister: propTypes.func,
  cellHighlight: propTypes.bool,
  setError: propTypes.func,
};
